import { FiMessageSquare } from 'react-icons/fi';

const advancedSpeakingPart3Exercises = [
  {
    title: 'Band 8 Discussion Drill',
    type: 'writing',
    difficulty: 'Hard',
    bandLevel: 'Band 7.5-8.5',
    aiReviewFocus: ['idea development', 'balance', 'complex language', 'IELTS Part 3 depth'],
    instructions:
      'Draft fuller Part 3 answers with balanced reasoning, examples, and a clear line of argument.',
    questions: [
      {
        text: 'Question: "Why do some societies place more value on practical skills than on academic knowledge?"',
        example:
          'I think this often happens because practical skills produce immediate economic value. For example, societies facing labour shortages may prioritise technical training because it leads directly to employment. That said, academic knowledge still matters because it supports research and long-term innovation, so ideally the two should complement each other.',
        evaluationMode: 'ai-review',
        aiReviewFocus: ['cause analysis', 'balanced discussion', 'example support'],
        tip: 'Develop the answer beyond one reason by adding contrast or qualification.',
      },
      {
        text: 'Question: "How might cities need to change if more people work remotely in the future?"',
        example:
          'I would imagine that cities will gradually become less centred around traditional office districts. This is because fewer commuters may reduce demand for large business areas, while neighbourhood services could become more important. For instance, local co-working spaces and flexible public transport might expand. However, major cities will probably still attract people for networking and specialist jobs.',
        evaluationMode: 'ai-review',
        aiReviewFocus: ['speculation', 'future language', 'logical development'],
        tip: 'Use speculative language and show at least one likely consequence.',
      },
    ],
  },
];

export const speakingPart3Data = {
  courseId: 'speaking-part3',
  courseType: 'speaking-course',
  title: 'Part 3: Discussion',
  icon: FiMessageSquare,
  overview: {
    impact: 'Demonstrate advanced speaking ability',
    focus: 'Abstract discussion techniques',
    uses: 'IELTS Speaking Part 3',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Part 3 Format',
      content: `<p>Part 3 is a 4-5 minute discussion on abstract topics related to Part 2:</p>`,
      points: [
        'More abstract and general than Parts 1 & 2',
        'Examiner asks follow-up questions',
        'You need to give extended, developed answers',
        'Speculation, comparison, and analysis expected',
      ],
      examples: [
        {
          label: 'Part 2',
          text: 'Describe a book you enjoyed reading.',
        },
        {
          label: 'Part 3',
          text: 'Why do you think reading is important for children? How has technology affected reading habits?',
        },
      ],
    },
    {
      title: 'Types of Part 3 Questions',
      content: `<p>Be prepared for these question types:</p>`,
      table: {
        headers: ['Type', 'Example', 'What\'s Expected'],
        rows: [
          ['Opinion', 'Do you think...?', 'State and justify your view'],
          ['Compare', 'How do X and Y differ?', 'Identify similarities/differences'],
          ['Speculate', 'What might happen if...?', 'Consider future possibilities'],
          ['Explain', 'Why do people...?', 'Analyze reasons and causes'],
          ['Evaluate', 'Is X important?', 'Assess significance with examples'],
        ],
      },
    },
    {
      title: 'Structuring Your Answers',
      content: `<p>Use this framework for extended answers:</p>`,
      points: [
        '<strong>State your position:</strong> "I believe that..." / "In my opinion..."',
        '<strong>Give a reason:</strong> "This is because..." / "The main reason is..."',
        '<strong>Provide an example:</strong> "For instance..." / "Take [X] as an example..."',
        '<strong>Add depth:</strong> Concede opposing views, speculate, compare',
        '<strong>Conclude:</strong> Briefly restate or summarize',
      ],
      examples: [
        {
          label: 'Full Answer',
          text: '"I think reading is crucial for children because it develops their imagination and vocabulary. For example, children who read regularly often perform better academically. However, I also understand that some children prefer digital content, which can also be educational if used wisely. Overall, I believe traditional reading should remain a priority."',
        },
      ],
    },
    {
      title: 'Useful Phrases',
      content: `<p>Use these to sound more sophisticated:</p>`,
      table: {
        headers: ['Purpose', 'Phrases'],
        rows: [
          ['Giving opinion', 'In my view... / Personally, I believe... / From my perspective...'],
          ['Speculating', 'It\'s possible that... / I would imagine... / Perhaps...'],
          ['Comparing', 'In contrast to... / Unlike... / On the other hand...'],
          ['Conceding', 'While it\'s true that... / Although... / Having said that...'],
          ['Concluding', 'All in all... / To sum up... / On balance...'],
        ],
      },
    },
  ],
  exercises: [
    {
      title: 'Identify Question Types',
      type: 'select',
      instructions: 'What type of Part 3 question is this?',
      questions: [
        {
          text: '"How do you think technology will change education in the future?"',
          options: ['Speculate', 'Compare', 'Opinion'],
          answer: 'Speculate',
          explanation: 'Asking about future possibilities = speculation.',
        },
        {
          text: '"What are the differences between learning from books and learning online?"',
          options: ['Compare', 'Opinion', 'Explain'],
          answer: 'Compare',
          explanation: 'Asking about differences = comparison.',
        },
        {
          text: '"Why do some people prefer reading traditional books?"',
          options: ['Explain', 'Speculate', 'Evaluate'],
          answer: 'Explain',
          explanation: 'Asking "why" requires explanation of reasons.',
        },
        {
          text: '"Do you believe governments should invest more in arts education?"',
          options: ['Opinion', 'Compare', 'Speculate'],
          answer: 'Opinion',
          explanation: '"Do you believe" asks for your personal view.',
        },
        {
          text: '"Is it important for children to learn multiple languages?"',
          options: ['Evaluate', 'Speculate', 'Compare'],
          answer: 'Evaluate',
          explanation: 'Asking about importance = evaluation.',
        },
        {
          text: '"How do urban and rural lifestyles differ?"',
          options: ['Compare', 'Explain', 'Opinion'],
          answer: 'Compare',
          explanation: '"How do X and Y differ" = comparison.',
        },
      ],
    },
    {
      title: 'Choose the Best Response Starter',
      type: 'select',
      instructions: 'Which phrase best starts the answer?',
      questions: [
        {
          text: 'For a speculation question about the future',
          options: ['I would imagine that...', 'In my view...', 'The main difference is...'],
          answer: 'I would imagine that...',
          explanation: '"I would imagine" is perfect for speculation.',
        },
        {
          text: 'For a comparison question',
          options: ['While X..., Y...', 'Personally, I believe...', 'This is because...'],
          answer: 'While X..., Y...',
          explanation: '"While" sets up a clear comparison.',
        },
        {
          text: 'For an opinion question',
          options: ['In my view...', 'It\'s possible that...', 'Unlike X...'],
          answer: 'In my view...',
          explanation: '"In my view" clearly introduces your opinion.',
        },
      ],
    },
    {
      title: 'Complete the Response',
      type: 'fill-in',
      instructions: 'Complete with an appropriate linking phrase.',
      questions: [
        {
          text: 'I believe reading is important for children. ___, it develops vocabulary.',
          answer: 'This is because',
          acceptableAnswers: ['Because', 'One reason is that'],
          evaluationMode: 'multi-answer',
          explanation: '"This is because" introduces your reason.',
        },
        {
          text: 'Technology has many benefits. ___, it also creates challenges.',
          answer: 'However',
          acceptableAnswers: ['That said', 'Nevertheless'],
          evaluationMode: 'multi-answer',
          explanation: '"However" introduces a contrasting point.',
        },
        {
          text: 'I think digital learning will grow. ___, younger people prefer screens.',
          answer: 'For example',
          acceptableAnswers: ['For instance'],
          evaluationMode: 'multi-answer',
          explanation: '"For example" introduces supporting evidence.',
        },
      ],
    },
    {
      title: 'Evaluate Response Quality',
      type: 'select',
      instructions: 'Which response is better for Part 3?',
      questions: [
        {
          text: '"Do you think technology affects communication?"',
          options: [
            '"Yes, I think technology has significantly impacted how we communicate. The main reason is that smartphones allow instant messaging. For instance, people now prefer texting over phone calls. However, this has also reduced face-to-face interaction, which I think is concerning."',
            '"Yes, technology affects communication."',
          ],
          answer: '"Yes, I think technology has significantly impacted how we communicate. The main reason is that smartphones allow instant messaging. For instance, people now prefer texting over phone calls. However, this has also reduced face-to-face interaction, which I think is concerning."',
          explanation: 'Extended answers with reasons and examples score higher.',
        },
        {
          text: '"Why is education important?"',
          options: [
            '"From my perspective, education is fundamental because it equips individuals with essential skills. For example, literacy and numeracy are necessary for most jobs. Moreover, education promotes critical thinking, which helps people make informed decisions. In short, education is the foundation of personal and societal development."',
            '"Education is important because you need it to get a job."',
          ],
          answer: '"From my perspective, education is fundamental because it equips individuals with essential skills. For example, literacy and numeracy are necessary for most jobs. Moreover, education promotes critical thinking, which helps people make informed decisions. In short, education is the foundation of personal and societal development."',
          explanation: 'This answer shows range, structure, and depth.',
        },
      ],
    },
    {
      title: 'Build Complete Answers',
      type: 'writing',
      instructions: 'Write a structured answer using position + reason + example.',
      questions: [
        {
          text: 'Question: "Do you think technology has improved education?"',
          example: 'I firmly believe technology has significantly improved education. The main reason is accessibility—students can now access world-class resources from anywhere. For instance, online platforms like Coursera allow students in developing countries to take courses from top universities. However, I also recognize that technology creates challenges, such as the digital divide. On balance, though, I think the benefits outweigh the drawbacks.',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['clear opinion', 'development', 'example quality', 'balance'],
          tip: 'Include your opinion, reason, example, and a balanced conclusion.',
        },
        {
          text: 'Question: "How might reading habits change in the next 20 years?"',
          example: 'I would imagine that digital reading will become even more prevalent in the next two decades. This is largely because of convenience—e-readers and tablets are becoming more affordable and accessible. For example, younger generations already prefer consuming content on screens. That said, I don\'t think physical books will disappear entirely, as many people still value the tactile experience of reading a real book.',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['speculative language', 'future prediction', 'balanced reasoning'],
          tip: 'Use speculative language and consider multiple perspectives.',
        },
        {
          text: 'Question: "What are the advantages and disadvantages of working from home?"',
          example: 'Working from home has both clear advantages and disadvantages. On the positive side, employees save time and money on commuting. For instance, my neighbor saves two hours daily by working remotely. On the other hand, remote work can lead to isolation and difficulty separating work from personal life. All in all, I think a hybrid approach might be the best solution.',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['two-sided analysis', 'example support', 'conclusion quality'],
          tip: 'Present both sides with examples, then give your conclusion.',
        },
      ],
    },
  ],
  tips: [
    'Don\'t give one-word answers—always develop your response',
    'It\'s okay to ask for clarification if you don\'t understand',
    'Show you can discuss abstract ideas, not just personal experiences',
    'Use advanced vocabulary and complex sentences',
  ],
  dailyPractice: [
    'Practice answering abstract questions on various topics',
    'Develop opinions on current affairs and social issues',
    'Practice with Liwaza AI Speaking for real-time feedback',
    'Record yourself and evaluate depth of answers',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Part 3 Discussion: Techniques for abstract discussion including answering opinion, comparison, and speculation questions with structured, extended responses.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...speakingPart3Data,
      title: speakingPart3Data.title + ' (Foundations)',
      overview: {
        ...speakingPart3Data.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: speakingPart3Data.sections.slice(0, Math.max(2, Math.ceil(speakingPart3Data.sections.length / 2))),
      exercises: speakingPart3Data.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...speakingPart3Data,
      title: speakingPart3Data.title + ' (Advanced)',
      overview: {
        ...speakingPart3Data.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: speakingPart3Data.sections,
      exercises: [
        ...speakingPart3Data.exercises.map(ex => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        ...advancedSpeakingPart3Exercises,
      ],
      tips: [
        ...(speakingPart3Data.tips || []),
        'For Band 8+, add nuance by acknowledging another viewpoint before defending your own.',
        'Advanced tip: extend answers with cause, consequence, and example rather than repeating the same point.',
      ],
    };
  }
  return speakingPart3Data;
}
